import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { queryCollection } from '../lib/api';
import { Code, Copy, CheckCircle, Eye, Link as LinkIcon, QrCode } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

interface Organization {
  id: string;
  name?: string;
}

export default function EmbedFormPage() {
  const { user } = useAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [embedHeight, setEmbedHeight] = useState('800');
  const [copied, setCopied] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    const fetchOrganization = async () => {
      if (!user?.organization_id) return;
      
      try {
        const result = await queryCollection<Organization>('organizations', { id: user.organization_id });
        if (result?.[0]) {
          setOrganization(result[0]);
        }
      } catch (e) {
        console.error('Failed to fetch organization:', e);
      }
    };
    
    fetchOrganization();
  }, [user?.organization_id]);

  const baseUrl = window.location.origin;
  const formUrl = `${baseUrl}/report?org=${user?.organization_id}&name=${encodeURIComponent(organization?.name || 'Felanmälan')}`;

  const iframeCode = `<iframe 
  src="${formUrl}"
  width="100%"
  height="${embedHeight}px"
  frameborder="0"
  style="border: 1px solid #e2e8f0; border-radius: 12px;"
  title="Felanmälan"
></iframe>`;

  const linkCode = `<a href="${formUrl}" target="_blank" style="display: inline-block; background: #3b82f6; color: white; padding: 12px 24px; border-radius: 8px; text-decoration: none; font-weight: 500;">Rapportera fel</a>`;

  const handleCopy = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    toast.success('Kopierat till urklipp!');
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="embed-form-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Externt Formulär</h1>
          <p className="text-slate-500 mt-1">
            Bädda in felanmälningsformuläret på din egen webbplats
          </p>
        </div>
      </div>

      <div className="space-y-6 max-w-4xl">
        {/* Direct Link */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <LinkIcon size={24} className="text-blue-600" />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-slate-900">Direktlänk</h2>
              <p className="text-sm text-slate-500 mt-1 mb-4">
                Skicka denna länk till boende så kan de rapportera fel direkt
              </p>
              
              <div className="flex gap-2">
                <Input 
                  value={formUrl} 
                  readOnly 
                  className="font-mono text-sm bg-slate-50"
                />
                <Button 
                  variant="outline"
                  onClick={() => handleCopy(formUrl, 'link')}
                >
                  {copied === 'link' ? <CheckCircle size={18} /> : <Copy size={18} />}
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => window.open(formUrl, '_blank')}
                >
                  <Eye size={18} />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Iframe Embed */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Code size={24} className="text-purple-600" />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-slate-900">Inbäddningskod (iframe)</h2>
              <p className="text-sm text-slate-500 mt-1 mb-4">
                Kopiera och klistra in denna kod i din webbplats HTML
              </p>
              
              <div className="mb-4">
                <Label>Höjd (px)</Label>
                <Input 
                  type="number"
                  value={embedHeight}
                  onChange={(e) => setEmbedHeight(e.target.value)}
                  className="w-32 mt-1"
                />
              </div>
              
              <div className="relative">
                <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-sm overflow-x-auto font-mono whitespace-pre-wrap break-all">
                  {iframeCode}
                </pre>
                <Button 
                  size="sm"
                  variant="secondary"
                  className="absolute top-2 right-2"
                  onClick={() => handleCopy(iframeCode, 'iframe')}
                >
                  {copied === 'iframe' ? <CheckCircle size={16} /> : <Copy size={16} />}
                  <span className="ml-1">Kopiera</span>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Link Button Code */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <QrCode size={24} className="text-green-600" />
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-slate-900">Länkknapp</h2>
              <p className="text-sm text-slate-500 mt-1 mb-4">
                En stilren knapp som öppnar formuläret i nytt fönster
              </p>
              
              <div className="mb-4">
                <p className="text-sm text-slate-600 mb-2">Förhandsvisning:</p>
                <a 
                  href={formUrl} 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Rapportera fel
                </a>
              </div>
              
              <div className="relative">
                <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-sm overflow-x-auto font-mono whitespace-pre-wrap break-all">
                  {linkCode}
                </pre>
                <Button 
                  size="sm"
                  variant="secondary"
                  className="absolute top-2 right-2"
                  onClick={() => handleCopy(linkCode, 'button')}
                >
                  {copied === 'button' ? <CheckCircle size={16} /> : <Copy size={16} />}
                  <span className="ml-1">Kopiera</span>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-900">Förhandsvisning</h2>
            <Button 
              variant="outline"
              onClick={() => setShowPreview(!showPreview)}
            >
              <Eye size={18} className="mr-2" />
              {showPreview ? 'Dölj' : 'Visa'} förhandsvisning
            </Button>
          </div>
          
          {showPreview && (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <iframe
                src={formUrl}
                width="100%"
                height={embedHeight}
                frameBorder="0"
                title="Förhandsvisning av formulär"
              />
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h3 className="font-semibold text-blue-900 mb-3">Instruktioner</h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start gap-2">
              <span className="font-bold">1.</span>
              <span>Välj hur du vill integrera formuläret (direktlänk, iframe, eller knapp)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">2.</span>
              <span>Kopiera koden eller länken</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">3.</span>
              <span>Klistra in på din webbplats (WordPress, Squarespace, egen HTML, etc.)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold">4.</span>
              <span>Ärenden som skickas via formuläret visas automatiskt i systemet</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
